/**
 * Hook de efeito typewriter — digita texto letra por letra.
 * Respeita prefers-reduced-motion: mostra texto completo instantâneo.
 *
 * @param text - Texto completo a ser "digitado"
 * @param opts - Opções de velocidade, delay e callback
 */
import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseTypewriterOptions {
  /** Milissegundos entre cada caractere (padrão: 30) */
  speedMs?: number;
  /** Delay antes de começar a digitar em ms (padrão: 0) */
  startDelayMs?: number;
  /** Callback chamado quando typewriter completa */
  onComplete?: () => void;
}

export interface UseTypewriterResult {
  /** Texto exibido até o momento (cresce letra por letra) */
  displayed: string;
  /** true quando todo o texto foi exibido */
  isComplete: boolean;
  /** Função para pular direto ao texto completo */
  skip: () => void;
}

/** Detecta preferência do usuário por menos animações */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useTypewriter(
  text: string,
  opts: UseTypewriterOptions = {},
): UseTypewriterResult {
  const { speedMs = 30, startDelayMs = 0, onComplete } = opts;

  const [displayed, setDisplayed] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const markComplete = useCallback((fullText: string) => {
    setDisplayed(fullText);
    setIsComplete(true);
    onCompleteRef.current?.();
  }, []);

  const skip = useCallback(() => {
    markComplete(text);
  }, [text, markComplete]);

  useEffect(() => {
    // Reseta ao mudar o texto
    setDisplayed('');
    setIsComplete(false);

    if (!text) {
      markComplete('');
      return;
    }

    // Sem animação quando prefers-reduced-motion
    if (prefersReducedMotion()) {
      markComplete(text);
      return;
    }

    let cancelled = false;
    let charIndex = 0;
    let delayTimer: ReturnType<typeof setTimeout>;
    let animFrame: number;

    const typeNextChar = () => {
      if (cancelled) return;
      if (charIndex >= text.length) {
        markComplete(text);
        return;
      }
      charIndex++;
      setDisplayed(text.slice(0, charIndex));

      // Próximo caractere após speedMs usando RAF + timeout
      animFrame = requestAnimationFrame(() => {
        if (cancelled) return;
        delayTimer = setTimeout(typeNextChar, speedMs);
      });
    };

    // Delay inicial antes de começar
    delayTimer = setTimeout(() => {
      if (!cancelled) typeNextChar();
    }, startDelayMs);

    return () => {
      cancelled = true;
      clearTimeout(delayTimer);
      if (animFrame) cancelAnimationFrame(animFrame);
    };
  }, [text, speedMs, startDelayMs, markComplete]);

  return { displayed, isComplete, skip };
}
